<script setup>
/**
 * 任务详情面板：任务列表右侧和任务树节点共用。看详情，有权限的人直接认领、修改、汇报进度、指派、删除（撤销），
 * 也能新建任务或子任务。数据变了通知父页面刷新（changed），新建成功告诉父页面新任务 id（created）。
 */
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, Card, ConfirmAction, Input, Select, Textarea } from '@drama/ui'
import { api, currentGuild, inGuild, session } from '../api.js'

const props = defineProps({ taskId: { type: String, default: null }, creating: { type: Boolean, default: false } })
const emit = defineEmits(['close', 'changed', 'created'])
const { t, locale } = useI18n()
const members = ref([])
const detail = ref(null)
const binding = ref(null)
const mode = ref('view')
const form = reactive({ title: '', spec: '', acceptance: '', reward: 0, funding: 'personal', holder_user_id: '', client_request_key: '' })
const busy = ref(false)
const error = ref('')
const owner = ref('')
const percent = ref(0)
const workPath = (suffix) => inGuild(`/api/voko/work${suffix}`)
const task = computed(() => (detail.value === null ? null : detail.value.task))
const me = computed(() => (session.user === null ? null : session.user.id))
const mine = computed(() => task.value !== null && task.value.holder_user_id === me.value)
const publisher = computed(() => task.value !== null && task.value.publisher_user_id === me.value)
const live = computed(() => task.value !== null && !['settled', 'cancelled'].includes(task.value.status))
const canEdit = computed(() => publisher.value && ['open', 'claimed', 'rework'].includes(task.value.status))
const canDelete = computed(() => publisher.value && ['open', 'claimed', 'submitted', 'rework'].includes(task.value.status))
const canAddChild = computed(() => mine.value && ['claimed', 'rework'].includes(task.value.status))
const canProgress = computed(() => mine.value && ['claimed', 'rework'].includes(task.value.status))
const ownerOptions = computed(() => [{ value: '', label: t('okr.unassigned') }, ...members.value.filter((item) => item.can_assign === 1).map((item) => ({ value: item.user_id, label: item.handle }))])
const person = (id) => (id === null ? t('okr.unassigned') : (members.value.find((item) => item.user_id === id) ?? { handle: id }).handle)
const date = (value) => (value === null ? '' : new Date(value).toLocaleDateString(locale.value))

async function run(body) {
  if (busy.value) return
  busy.value = true
  error.value = ''
  try { await body() } catch (err) { error.value = err instanceof Error ? err.message : String(err) } finally { busy.value = false }
}
async function loadDetail() {
  if (props.taskId === null || currentGuild.slug === null) { detail.value = null; binding.value = null; return }
  try {
    const [next, sync] = await Promise.all([api('GET', workPath(`/${props.taskId}`)), api('GET', workPath(`/${props.taskId}/sync`))])
    detail.value = next
    binding.value = sync.binding
    owner.value = next.task.holder_user_id === null ? '' : next.task.holder_user_id
    percent.value = next.task.percent === 100 ? 90 : next.task.percent
  } catch (err) { detail.value = null; error.value = err instanceof Error ? err.message : String(err) }
}
async function loadMembers() {
  if (currentGuild.slug === null || session.user === null) return
  members.value = (await api('GET', workPath('/members'))).members
}
async function reload() { await loadDetail(); emit('changed') }

watch([() => currentGuild.slug, me], () => { mode.value = 'view'; loadMembers().then(loadDetail) }, { immediate: true })
watch(() => props.taskId, () => { if (mode.value !== 'create') mode.value = 'view'; loadDetail() })
watch(() => props.creating, (value) => { if (value) startCreate() }, { immediate: true })

function startCreate(parent = null) {
  Object.assign(form, { title: '', spec: '', acceptance: '', reward: 0, funding: 'personal', holder_user_id: '', client_request_key: crypto.randomUUID() })
  mode.value = parent === null ? 'create' : 'child'
}
function startEdit() {
  Object.assign(form, { title: task.value.title, spec: task.value.spec, acceptance: task.value.acceptance.join('\n') })
  mode.value = 'edit'
}
function close() { mode.value = 'view'; emit('close') }
const lines = (text) => text.split('\n').map((line) => line.trim()).filter((line) => line !== '')
const act = (name, fields = {}) => run(async () => {
  await api('POST', workPath(`/${task.value.id}/actions`), { action: name, version: task.value.version, ...fields })
  mode.value = 'view'
  await reload()
})
const save = () => act('edit', { title: form.title, spec: form.spec, acceptance: lines(form.acceptance) })
const remove = async () => {
  await api('POST', workPath(`/${task.value.id}/actions`), { action: 'cancel', version: task.value.version })
  await reload()
}
const create = () => run(async () => {
  const parent_task_id = mode.value === 'child' ? task.value.id : null
  const result = await api('POST', workPath('/'), { ...form, parent_task_id, reward: Number(form.reward), holder_user_id: form.holder_user_id === '' ? null : form.holder_user_id, acceptance: lines(form.acceptance) })
  mode.value = 'view'
  emit('created', result.task.id)
})
</script>

<template>
<aside v-if="task !== null || mode === 'create'" class="tl-panel" :aria-label="t('okr.table.detail')">
  <Card>
    <div class="tl-panel-head"><h2>{{ mode === 'create' ? t('okr.newTask') : mode === 'child' ? t('okr.newChild') : task.title }}</h2><Button variant="ghost" size="sm" :aria-label="t('okr.dismiss')" @click="close">✕</Button></div>
    <p v-if="error" role="alert">{{ error }}</p>

    <form v-if="mode !== 'view'" class="tl-form" @submit.prevent="mode === 'edit' ? save() : create()">
      <label>{{ t('okr.taskTitle') }}<Input v-model="form.title" :disabled="busy" required maxlength="200" /></label>
      <label>{{ t('okr.description') }}<Textarea v-model="form.spec" :disabled="busy" /></label>
      <label>{{ t('okr.acceptance') }}<Textarea v-model="form.acceptance" :disabled="busy" /></label>
      <template v-if="mode !== 'edit'">
        <label>{{ t('okr.owner') }}<Select v-model="form.holder_user_id" :disabled="busy" :aria-label="t('okr.owner')" :options="ownerOptions" /></label>
        <label>{{ t('okr.reward') }}<Input v-model="form.reward" :disabled="busy" type="number" min="0" max="1000000000" /></label>
        <label v-if="Number(form.reward) > 0">{{ t('okr.funding') }}<Select v-model="form.funding" :disabled="busy" :aria-label="t('okr.funding')" :options="[{ value: 'personal', label: t('okr.personalWallet') }, { value: 'guild', label: t('okr.guildTreasury') }]" /></label>
      </template>
      <div class="tl-actions"><Button type="submit" :disabled="busy || form.title.trim() === ''">{{ mode === 'edit' ? t('okr.save') : t('okr.publish') }}</Button><Button variant="ghost" :disabled="busy" @click="mode === 'create' ? close() : (mode = 'view')">{{ t('okr.table.cancel') }}</Button></div>
    </form>

    <template v-else>
      <p><Badge>{{ t(`okr.status.${task.status}`) }}</Badge> <span v-if="task.node_id" class="tl-mono">{{ task.node_id }}</span></p>
      <p class="tl-copy">{{ task.spec }}</p>
      <dl class="tl-facts">
        <dt>{{ t('okr.owner') }}</dt><dd>{{ person(task.holder_user_id) }}</dd>
        <dt>{{ t('okr.publisher') }}</dt><dd>{{ person(task.publisher_user_id) }}</dd>
        <dt>{{ t('okr.table.progress') }}</dt><dd>{{ task.percent }}%</dd>
        <dt>{{ t('okr.table.due') }}</dt><dd>{{ date(task.due_on) || '—' }}</dd>
        <dt>{{ t('okr.reward') }}</dt><dd>{{ task.reward }}</dd>
      </dl>
      <ul v-if="task.acceptance.length > 0"><li v-for="item in task.acceptance" :key="item">{{ item }}</li></ul>
      <p v-if="binding !== null && binding.status === 'conflict'" role="alert">{{ t('okr.sync.status.conflict') }} — <router-link :to="`/work/${task.id}`">{{ t('okr.table.openFull') }}</router-link></p>
      <p v-if="task.source_state === 'source_deleted'" role="alert">{{ t('okr.sync.sourceDeleted') }}</p>

      <div class="tl-actions">
        <Button v-if="task.status === 'open' && task.percent < 100" :disabled="busy" @click="act('claim')">{{ t('okr.claim') }}</Button>
        <Button v-if="canEdit" variant="secondary" :disabled="busy" @click="startEdit">{{ t('okr.edit') }}</Button>
        <Button v-if="canAddChild" variant="secondary" :disabled="busy" @click="startCreate(task)">{{ t('okr.newChild') }}</Button>
        <ConfirmAction v-if="canDelete" :label="t('okr.table.delete')" :action="remove" :confirm-text="t('okr.table.deleteConfirm', { title: task.title })" :cancel-label="t('okr.table.cancel')" :ok-label="t('okr.table.delete')" :pending-label="t('okr.table.working')" danger />
      </div>
      <div v-if="canProgress" class="tl-inline"><label>{{ t('okr.table.progress') }}<Select v-model="percent" :options="[0, 25, 50, 75, 90].map((value) => ({ value, label: `${value}%` }))" :aria-label="t('okr.table.progress')" size="sm" /></label><Button size="sm" variant="secondary" :disabled="busy || percent === task.percent" @click="act('progress', { percent })">{{ t('okr.table.report') }}</Button></div>
      <div v-if="publisher && live" class="tl-inline"><label>{{ t('okr.owner') }}<Select v-model="owner" :options="ownerOptions" :aria-label="t('okr.owner')" size="sm" /></label><Button size="sm" variant="secondary" :disabled="busy || owner === '' || owner === task.holder_user_id" @click="act('assign', { holder_user_id: owner })">{{ t('okr.assign') }}</Button></div>
      <p><router-link :to="`/work/${task.id}`">{{ t('okr.table.openFull') }}</router-link></p>
    </template>
  </Card>
</aside>
</template>

<style scoped>
.tl-panel { position: sticky; top: var(--drama-space-lg); min-width: 0; }
.tl-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--drama-space-sm); }
.tl-panel-head h2 { overflow-wrap: anywhere; }
.tl-form { display: grid; gap: var(--drama-space-md); }
.tl-form label, .tl-inline label { display: grid; gap: var(--drama-space-sm); }
.tl-actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); margin-block: var(--drama-space-md); }
.tl-inline { display: flex; align-items: end; gap: var(--drama-space-sm); margin-block: var(--drama-space-md); }
.tl-facts { display: grid; grid-template-columns: max-content 1fr; gap: var(--drama-space-xs) var(--drama-space-md); }
.tl-facts dt { color: var(--drama-color-text-muted); }
.tl-copy { white-space: pre-wrap; overflow-wrap: anywhere; }
.tl-mono { font-family: var(--drama-font-mono, monospace); color: var(--drama-color-text-muted); }
@media (max-width: 1100px) {
  .tl-panel { position: static; }
}
</style>
